import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Mail, KeyRound, AlertCircle, CheckCircle, X, LogIn, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

const CODE_LENGTH = 6;

/**
 * Passwordless sign-in: enter an email, receive a 6-digit code, type it in.
 * Google is offered alongside and needs no email delivery at all, so it keeps
 * working if SMTP ever fails again.
 *
 * There is no separate "sign up" - the first time an address is used, Supabase
 * creates the account (shouldCreateUser). Existing Quick Join users keep their
 * votes and starred movies automatically, because both key on the email
 * address; their name and avatar are carried over by ensureUserProfile.
 */
const PasswordlessAuth = ({ isVisible, onClose, onSignedIn = null }) => {
  const [step, setStep] = useState('email'); // 'email' | 'code'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState('');
  const [success, setSuccess] = useState('');

  const { signInWithPasscode, verifyPasscode, signInWithGoogle, loading, error } = useAuth();

  const reset = () => {
    setStep('email');
    setEmail('');
    setCode('');
    setLocalError('');
    setSuccess('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLocalError('');
    setSuccess('');

    const trimmed = email.trim();
    if (!trimmed) {
      setLocalError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setLocalError('That does not look like a valid email address');
      return;
    }

    const result = await signInWithPasscode(trimmed);
    if (!result.error) {
      setStep('code');
      setSuccess(`We sent a ${CODE_LENGTH}-digit code to ${trimmed}`);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (code.trim().length !== CODE_LENGTH) {
      setLocalError(`Enter the ${CODE_LENGTH}-digit code from your email`);
      return;
    }

    const result = await verifyPasscode(email, code);
    if (!result.error) {
      setSuccess('Signed in!');
      setTimeout(() => {
        if (onSignedIn) onSignedIn();
        handleClose();
      }, 900);
    }
  };

  const handleResend = async () => {
    setLocalError('');
    setSuccess('');
    const result = await signInWithPasscode(email);
    if (!result.error) setSuccess('New code sent - check your email');
  };

  if (!isVisible) return null;

  const currentError = localError || error;
  const inputClass =
    'w-full pl-10 pr-4 py-3 bg-gray-700 border border-orange-500 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-400 focus:ring-opacity-50';

  /* Rendered through a portal to document.body.
   *
   * TabNavigation sits inside <div className="relative z-10"> in App.js, and a
   * positioned element with a z-index CREATES A STACKING CONTEXT. Any modal
   * rendered inside it has its z-50 confined to that context, so page content
   * painted above z-10 covers the modal - raising the number would not help.
   * A portal moves the node out to <body>, escaping every ancestor context. */
  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl max-w-md w-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-900 to-orange-900 px-6 py-4 border-b-2 border-orange-500">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-orange-100 flex items-center gap-2">
              <LogIn size={20} />
              {step === 'email' ? 'Sign In' : 'Enter Your Code'}
            </h2>
            <button
              onClick={handleClose}
              className="text-orange-300 hover:text-orange-100 transition-colors"
              aria-label="Close"
            >
              <X size={24} />
            </button>
          </div>
          <p className="text-orange-200 text-sm mt-1">
            {step === 'email'
              ? 'No password needed - we will email you a code'
              : `Sent to ${email}`}
          </p>
        </div>

        <div className="p-6">
          {step === 'email' ? (
            <>
              <form onSubmit={handleSendCode} className="space-y-4">
                <div>
                  <label htmlFor="pw-email" className="block text-orange-300 text-sm font-bold mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 text-gray-400" size={16} />
                    <input
                      id="pw-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      autoComplete="email"
                      className={inputClass}
                      disabled={loading}
                    />
                  </div>
                </div>

                {currentError && (
                  <div className="p-3 bg-red-900 border border-red-600 rounded-lg flex items-center gap-2">
                    <AlertCircle size={20} className="text-red-400 flex-shrink-0" />
                    <span className="text-red-200 text-sm">{currentError}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-orange-600 hover:bg-orange-500 disabled:bg-gray-600 text-white font-bold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Mail size={16} />
                  {loading ? 'Sending...' : 'Email me a code'}
                </button>
              </form>

              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-gray-600" />
                <span className="text-gray-400 text-xs uppercase tracking-wide">or</span>
                <div className="flex-1 h-px bg-gray-600" />
              </div>

              <button
                onClick={signInWithGoogle}
                disabled={loading}
                className="w-full bg-white hover:bg-gray-100 disabled:bg-gray-400 text-gray-800 font-bold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-3"
              >
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#4285F4" d="M45.1 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h11.8c-.5 2.8-2 5.1-4.4 6.7v5.5h7.1c4.1-3.8 6.6-9.4 6.6-16.2z" />
                  <path fill="#34A853" d="M24 46c5.9 0 10.9-2 14.5-5.3l-7.1-5.5c-2 1.3-4.5 2.1-7.4 2.1-5.7 0-10.5-3.8-12.2-9H4.5v5.7C8.1 41.1 15.5 46 24 46z" />
                  <path fill="#FBBC05" d="M11.8 28.3c-.4-1.3-.7-2.7-.7-4.3s.3-3 .7-4.3v-5.7H4.5A22 22 0 0 0 2 24c0 3.6.9 6.9 2.5 9.9l7.3-5.6z" />
                  <path fill="#EA4335" d="M24 10.7c3.2 0 6.1 1.1 8.4 3.3l6.3-6.3C34.9 4.1 29.9 2 24 2 15.5 2 8.1 6.9 4.5 14.1l7.3 5.7c1.7-5.2 6.5-9.1 12.2-9.1z" />
                </svg>
                Continue with Google
              </button>

              <p className="text-gray-500 text-xs text-center mt-5">
                First time here? Just enter your email - there is no separate sign-up.
              </p>
            </>
          ) : (
            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <label htmlFor="pw-code" className="block text-orange-300 text-sm font-bold mb-2">
                  {CODE_LENGTH}-Digit Code
                </label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-3 text-gray-400" size={16} />
                  <input
                    id="pw-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
                    placeholder="123456"
                    className={`${inputClass} tracking-[0.4em] text-lg`}
                    disabled={loading}
                    autoFocus
                  />
                </div>
              </div>

              {currentError && (
                <div className="p-3 bg-red-900 border border-red-600 rounded-lg flex items-center gap-2">
                  <AlertCircle size={20} className="text-red-400 flex-shrink-0" />
                  <span className="text-red-200 text-sm">{currentError}</span>
                </div>
              )}

              {success && (
                <div className="p-3 bg-green-900 border border-green-600 rounded-lg flex items-center gap-2">
                  <CheckCircle size={20} className="text-green-400 flex-shrink-0" />
                  <span className="text-green-200 text-sm">{success}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || code.length !== CODE_LENGTH}
                className="w-full bg-orange-600 hover:bg-orange-500 disabled:bg-gray-600 text-white font-bold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <KeyRound size={16} />
                {loading ? 'Checking...' : 'Sign In'}
              </button>

              <div className="flex items-center justify-between pt-1">
                <button
                  type="button"
                  onClick={() => { setStep('email'); setCode(''); setLocalError(''); setSuccess(''); }}
                  className="text-gray-400 hover:text-orange-300 text-sm flex items-center gap-1"
                >
                  <ArrowLeft size={14} /> Use a different email
                </button>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={loading}
                  className="text-orange-400 hover:text-orange-300 text-sm font-semibold disabled:text-gray-500"
                >
                  Resend code
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PasswordlessAuth;
